import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";

import type { DocSolutionAreaUpdatePayload } from "@xop/dashboard-shared";

import { updateSolutionArea } from "./api.js";

/**
 * Rename + visibility editor for a single solution area. Mirrors
 * `EditCategoryDialog`: PATCHes only the fields that actually changed
 * (no-op when nothing changed so we don't ask Rails to re-derive the slug
 * from the same string).
 *
 * Implementation note: the visibility control stays a NATIVE
 * `<input type="checkbox">` wrapped in a `<label>` (rather than MUI's
 * Checkbox/FormControlLabel) so the colocated spec can reach it via
 * `getByText("Visible…").closest("label").querySelector("input[type=checkbox]")`.
 */
export function EditSolutionAreaDialog({
  target,
  onClose,
  onSaved,
}: {
  target: {
    id: number;
    name: string;
    visible: boolean;
    /** Reseller-portal visibility; absent reads as `false`, matching the
     * server default. */
    visibleToResellers?: boolean;
  } | null;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element | null {
  const [name, setName] = useState("");
  const [visible, setVisible] = useState(true);
  const [resellerVisible, setResellerVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initializedId = useRef<number | null>(null);

  if (target && initializedId.current !== target.id) {
    initializedId.current = target.id;
    setName(target.name);
    setVisible(target.visible);
    setResellerVisible(target.visibleToResellers === true);
    setError(null);
  }
  if (!target && initializedId.current !== null) {
    initializedId.current = null;
  }

  const mutation = useMutation({
    mutationFn: async () => {
      if (!target) throw new Error("No solution area to edit");
      const trimmed = name.trim();
      const solution_area: DocSolutionAreaUpdatePayload["solution_area"] = {};
      if (trimmed && trimmed !== target.name) solution_area.name = trimmed;
      if (visible !== target.visible) {
        solution_area.visible_in_customer_portal = visible;
      }
      if (resellerVisible !== (target.visibleToResellers === true)) {
        solution_area.visible_in_reseller_portal = resellerVisible;
      }
      if (Object.keys(solution_area).length === 0) return null;
      return updateSolutionArea(target.id, { solution_area });
    },
    onSuccess: () => {
      setError(null);
      onSaved();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Could not update solution area");
    },
  });

  if (!target) return null;

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={!!target} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography component="span" sx={{ display: "block", fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em" }}>
          Rename solution area
        </Typography>
        <Typography component="span" sx={{ display: "block", fontSize: 12, fontWeight: 400, color: "text.disabled" }}>
          Updates the pillar name + visibility. Slug auto-re-derives from the new name on the Rails side.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box
          component="form"
          sx={{ display: "grid", gap: 2, pt: 1 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            mutation.mutate();
          }}
        >
          <Box sx={{ display: "grid", gap: 0.75 }}>
            <TextField
              id="edit-solution-area-name"
              label="Name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Public Safety"
              inputProps={{ required: true }}
              fullWidth
            />
            <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
              Renaming re-slugs the pillar — a shared `?solution=` deep link to
              the old slug will fall back to “All”. Tagged docs keep their
              association.
            </Typography>
          </Box>

          <Box
            component="label"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.5),
              p: 1.5,
              fontSize: 14,
            }}
          >
            <Box
              component="input"
              type="checkbox"
              checked={visible}
              onChange={(e) => setVisible(e.target.checked)}
              sx={{ mt: 0.25 }}
            />
            <Box component="span">
              <Box component="span" sx={{ fontWeight: 500 }}>
                Visible in customer portal
              </Box>
              <Box component="span" sx={{ display: "block", fontSize: 12, color: "text.disabled" }}>
                When off, the pillar is hidden from customer-facing surfaces.
              </Box>
            </Box>
          </Box>

          <Box
            component="label"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.5),
              p: 1.5,
              fontSize: 14,
            }}
          >
            <Box
              component="input"
              type="checkbox"
              checked={resellerVisible}
              onChange={(e) => setResellerVisible(e.target.checked)}
              data-testid="solution-area-visible-resellers"
              sx={{ mt: 0.25 }}
            />
            <Box component="span">
              <Box component="span" sx={{ fontWeight: 500 }}>
                Visible in reseller portal
              </Box>
              <Box component="span" sx={{ display: "block", fontSize: 12, color: "text.disabled" }}>
                Independent of the customer switch. When on, this pillar
                appears as a filter chip for XOP Reseller partners.
              </Box>
            </Box>
          </Box>

          {error && (
            <Box
              sx={{
                borderRadius: 1,
                border: 1,
                borderColor: "error.main",
                bgcolor: (t) => alpha(t.palette.error.main, 0.08),
                px: 1.5,
                py: 1,
                fontSize: 12,
                color: "error.main",
              }}
            >
              {error}
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              borderTop: 1,
              borderColor: "divider",
              mx: -3,
              mb: -2,
              px: 3,
              py: 1.5,
            }}
          >
            <Button type="button" variant="text" color="inherit" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={!name.trim() || mutation.isPending}>
              {mutation.isPending ? "Saving…" : "Save changes"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
